import React, {
  useState,
  createContext,
  useRef,
  useEffect,
  useCallback,
} from "react";
import { parseCookies } from "nookies";
import Swal from "sweetalert2";
import AlertResponses from "../jsx/components/Alert";
import { WEBSOCKET_URL } from "../api/Urls";
import useNotificationSounds from "../hooks/NotificationsHook";
import NotificationService from "../services/NotificationsServices";
import { ChangeBotStatus } from "../services/WaBottyServices";

const RECONNECT_INTERVAL = 5000;
const PING_INTERVAL = 10000;

const NotificationsContext = createContext();

export function NotificationsProvider({ children }) {
  // Verificar se é uma rota pública
  const publicRoutes = ["/login", "/register", "/activate", "/checkout", "/signup", "/politica-de-privacidade", "/termos-de-uso"];
  const currentPath = window.location.pathname;
  const isPublicRoute = publicRoutes.includes(currentPath);

  const { token: _token } = parseCookies();
  const { MA_AID: account_id } = parseCookies();

  // Função para obter a palavra-chave configurada do localStorage
  const getConfiguredKeyword = useCallback(() => {
    try {
      const accsettings = localStorage.getItem("__accsettings");
      if (accsettings) {
        const settings = JSON.parse(accsettings);
        return settings?.notifications?.keyword || null;
      }
    } catch (error) {
      console.error("Erro ao ler configurações do localStorage:", error);
    }
    return null;
  }, []);

  const {
    playNotificationSound,
    playHumanRequestSound,
    pauseHumanRequestSound,
  } = useNotificationSounds();

  // Refs
  const token = useRef(_token);
  const ws = useRef(null);
  const pingIntervalId = useRef(null);
  const reconnectTimeoutId = useRef(null);
  const helpData = useRef(null);
  const humanRequestNotification = useRef(false);

  // State
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [notify, setNotify] = useState({
    title: "",
    message: "",
    type: "",
    visible: false,
  });
  const [wsReadyState, setWsReadyState] = useState(0);
  const [unreadCounts, setUnreadCounts] = useState({});

  const Toast = Swal.mixin({
    toast: true,
    position: "bottom-end",
    timer: 30000,
    timerProgressBar: true,
    showConfirmButton: true,
    didOpen: (toast) => {
      toast.onmouseenter = Swal.stopTimer;
      toast.onmouseleave = Swal.resumeTimer;
    },
    customClass: {
      popup: "swal2-toast-custom",
      title: "swal2-toast-title",
      htmlContainer: "swal2-toast-content",
    },
  });

  // WebSocket message handler otimizado
  const handleWebSocketMessage = useCallback(
    (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data && typeof data === "object") {
          // Verifica se a notificação tem account_id e se corresponde ao usuario atual
          const isForCurrentStore =
            !data.account_id ||
            data.account_id.toString() === account_id.toString();
          if (!isForCurrentStore) {
            return; // Ignora notificações de outras lojas
          }
          if (data?.new_schedule) {
            Toast.fire({
              icon: "success",
              title: "Novo Agendamento!",
              html: "Você tem um novo agendamento! Atualize sua agenda.",
            });
          } else if (
            data.wa_notify &&
            typeof data.wa_notify.event === "string" &&
            data.wa_notify.event.startsWith("cloud_api_")
          ) {
            // Alertas de canal Cloud API (backend Fase 2). NÃO é mensagem.
            // Emite postMessage que ChannelAlertsContext escuta para popular
            // o feed/banner/toast no editor de agente.
            window.postMessage(
              {
                type: "channel_alert",
                event: data.wa_notify.event,
                severity: data.wa_notify.severity || "info",
                message: data.wa_notify.message || "",
                cloud_account_id: data.wa_notify.cloud_account_id,
                phone: data.wa_notify.phone,
                timestamp:
                  data.wa_notify.timestamp || Math.floor(Date.now() / 1000),
                // mantém payload completo para handlers contextuais
                payload: data.wa_notify,
              },
              "*",
            );
          } else if (data.wa_notify) {
            // window.postMessage({ playWaSound: true }, "*");
            playNotificationSound();
            window.postMessage({
              newWaMessage: {
                message: data.wa_notify.message,
                phone: data.wa_notify.from_me
                  ? data.wa_notify.to
                  : data.wa_notify.from,
                from: data.wa_notify.from,
                to: data.wa_notify.to,
                from_me: data.wa_notify.from_me,
                account_id: data.wa_notify.account_id,
                timestamp:
                  data.wa_notify.timestamp || Math.floor(Date.now() / 1000),
                has_media: data.wa_notify.has_media || false,
                media_url: data.wa_notify.media_url || null,
                media_type: data.wa_notify.media_type || null,
              },
            });
          } else if (data.contact_updated) {
            // Enriquecimento async de name/picture_url de contatos
            // Messenger/Instagram. Front atualiza in-place o item
            // da ChatList sem refetch geral.
            window.postMessage(
              {
                contactUpdated: {
                  account_id: data.contact_updated.account_id,
                  contact_id: data.contact_updated.contact_id,
                  channel: data.contact_updated.channel,
                  channel_user_id: data.contact_updated.channel_user_id,
                  name: data.contact_updated.name || '',
                  picture_url: data.contact_updated.picture_url || '',
                },
              },
              '*',
            );
          } else if (data.wa_sent_notify) {
            window.postMessage({
              newWaSentMessage: {
                message: data.wa_sent_notify.message,
                phone: data.wa_sent_notify.from_me
                  ? data.wa_sent_notify.to
                  : data.wa_sent_notify.from,
                from: data.wa_sent_notify.from,
                to: data.wa_sent_notify.to,
                from_me: data.wa_sent_notify.from_me,
                account_id: data.wa_sent_notify.account_id,
                has_media: data.wa_sent_notify.has_media || false,
                media_url: data.wa_sent_notify.media_url || null,
                media_type: data.wa_sent_notify.media_type || null,
              },
            });
          } else if (data.human_support_request) {
            humanSupportRequest(data.human_support_request.message);
          } else if (data?.new_schedule_confirmation) {
            Toast.fire({
              icon: "success",
              title: "Nova Confirmação!",
              html: data?.new_schedule_confirmation.message,
            });
          } else if (data?.new_schedule_cancellation) {
            Toast.fire({
              icon: "info",
              title: "Cliente cancelou!",
              html: data?.new_schedule_cancellation.message,
            });
          } else if (data.wa_notify_success_send) {
            window.postMessage({
              waMessageSuccess: data.wa_notify_success_send.message,
            });
          } else if (data.wa_notify_error_send) {
            window.postMessage({
              waMessageError: data.wa_notify_error_send.message,
            });
          } else if (data?.new_crm_contact) {
            Toast.fire({
              icon: "success",
              title: "Novo Contato!",
              html: data.new_crm_contact.message || "Um novo contato foi adicionado automaticamente em seu CRM.",
            });
            if (data.new_crm_contact.contact_data) {
              window.postMessage({
                newCrmContact: data.new_crm_contact.contact_data,
              }, "*");
            }
          } else if (data.pix_id) {
            window.postMessage({ pix_id: data.pix_id });
          } else if (data.customer_requested_help) {
            helpData.current = data.customer_requested_help;
            setShowHelpModal(true);
          } else if (data.order_refund_error) {
            const { order_number, customer_name, message } =
              data.order_refund_error.message;
            Swal.fire({
              icon: "warning",
              title: "Oops!",
              html: `O pedido (#${order_number}) do <strong>${customer_name}</strong> não pode ser estornado. Motivo: ${message}`,
              showCancelButton: true,
              confirmButtonColor: "var(--primary)",
              cancelButtonColor: "var(--subtitles)",
              confirmButtonText: "Acessar painel",
              cancelButtonText: "Cancelar",
            }).then((value) => {
              if (value.isConfirmed) {
                window.open("https://www.asaas.com/dashboard/index");
              }
            });
          } else if (data.transfer_notification) {
            // Notificação de transferência de atendimento recebida
            const transfer = data.transfer_notification;
            playNotificationSound();

            Swal.fire({
              icon: "info",
              title: "Nova Transferência Recebida!",
              html: `
                <div style="text-align: left; padding: 10px 0;">
                  <p><strong>${transfer.from_attendant || "Um atendente"}</strong> transferiu um atendimento para você.</p>
                  <hr style="margin: 12px 0; border-color: #eee;" />
                  <p><strong>Contato:</strong> ${transfer.contact_name || transfer.contact_phone || "N/A"}</p>
                  ${transfer.contact_phone ? `<p><strong>Telefone:</strong> ${transfer.contact_phone}</p>` : ""}
                  ${transfer.from_department ? `<p><strong>Departamento:</strong> ${transfer.from_department}</p>` : ""}
                  ${transfer.reason ? `<p><strong>Motivo:</strong> ${transfer.reason}</p>` : ""}
                </div>
              `,
              showCancelButton: true,
              confirmButtonColor: "var(--primary)",
              cancelButtonColor: "var(--subtitles)",
              confirmButtonText: "Ir para Atendimento",
              cancelButtonText: "Depois",
            }).then((result) => {
              if (result.isConfirmed) {
                window.location.href = "/service-center";
              }
            });

            // Emitir evento para o ServiceCenter atualizar a lista
            window.postMessage({
              newTransfer: {
                contact_id: transfer.contact_id,
                contact_name: transfer.contact_name,
                contact_phone: transfer.contact_phone,
                from_attendant: transfer.from_attendant,
                from_attendant_id: transfer.from_attendant_id,
                from_department: transfer.from_department,
                reason: transfer.reason,
                timestamp: transfer.timestamp || Date.now(),
              }
            }, "*");
          }
        }
      } catch (error) {
        console.error("Error processing WebSocket message:", error);
      }
    },
    [account_id]
  );

  // WebSocket setup
  useEffect(() => {
    const setupWebSocket = () => {
      // Ler token fresco dos cookies a cada reconexão (o refresh pode ter renovado)
      const { token: freshToken } = parseCookies();
      if (!account_id || !freshToken || isPublicRoute) return;
      if (ws.current?.readyState === WebSocket.OPEN) return;

      token.current = freshToken;
      const socketUrl = `${WEBSOCKET_URL}notifications/?ct=1&token=${freshToken}&account_id=${account_id}`;
      ws.current = new WebSocket(socketUrl);

      ws.current.onopen = () => {
        setWsReadyState(WebSocket.OPEN);

        if (pingIntervalId.current) {
          clearInterval(pingIntervalId.current);
        }

        pingIntervalId.current = setInterval(() => {
          if (ws.current?.readyState === WebSocket.OPEN) {
            ws.current.send(JSON.stringify({ type: "ping" }));
          }
        }, PING_INTERVAL);
      };

      ws.current.onmessage = handleWebSocketMessage;

      ws.current.onclose = (event) => {
        setWsReadyState(WebSocket.CLOSED);

        if (pingIntervalId.current) {
          clearInterval(pingIntervalId.current);
        }

        // Token expirado/inválido (close code 4001 vindo do middleware):
        // não reconectar em loop — aguardar novo login renovar o cookie.
        if (event && event.code === 4001) return;

        reconnectTimeoutId.current = setTimeout(
          setupWebSocket,
          RECONNECT_INTERVAL
        );
      };

      ws.current.onerror = () => {
        ws.current?.close();
      };
    };

    if (!isPublicRoute) {
      setupWebSocket();
    }

    return () => {
      if (ws.current) ws.current.close();
      if (pingIntervalId.current) clearInterval(pingIntervalId.current);
      if (reconnectTimeoutId.current) clearTimeout(reconnectTimeoutId.current);
    };
  }, [handleWebSocketMessage, isPublicRoute]);

  useEffect(() => {
    const tabWhatsapp = document.querySelector('[data-rb-event-key="5"]');
    const unread = Object.values(unreadCounts).some((valor) => valor > 0);

    if (unread) {
      if (tabWhatsapp) {
        let i = document.createElement("i");
        i.className = "fa fa-circle pulsate-bck";
        tabWhatsapp.appendChild(i);
      }
    } else {
      if (tabWhatsapp) {
        tabWhatsapp.querySelector(".fa-circle")?.remove();
      }
    }
  }, [unreadCounts]);

  const sendMessage = useCallback((message) => {
    if (ws.current?.readyState === WebSocket.OPEN) {
      ws.current.send(
        typeof message === "string" ? message : JSON.stringify(message)
      );
      return true;
    }
    console.warn("WebSocket is not connected. Message not sent:", message);
    return false;
  }, []);

  const humanSupportRequest = async (message) => {
    if (!humanRequestNotification.current) {
      humanRequestNotification.current = true;
      setTimeout(() => {
        playHumanRequestSound();
      }, 1000);
      Swal.fire({
        title: "Atendimento Humano!",
        html: message,
        icon: "info",
        showConfirmButton: true,
        confirmButtonText: "Entendi",
        allowOutsideClick: false,
        allowEscapeKey: false,
      }).then((result) => {
        if (result.isConfirmed) {
          pauseHumanRequestSound();
          // Cooldown de 2h após o usuário confirmar a Swal — evita re-popups
          // em sequência caso o LLM/cliente dispare múltiplas transferências.
          // 60s * 60min * 2h * 1000ms = 7_200_000 ms.
          // (Antes estava 60*60*2 = 7200ms = 7,2s — bug de unidade.)
          setTimeout(() => {
            humanRequestNotification.current = false;
          }, 60 * 60 * 1000 * 2);
        }
      });
    }
  };

  return (
    <NotificationsContext.Provider
      value={{
        notify,
        setNotify,
        helpData,
        showHelpModal,
        setShowHelpModal,
        wsReadyState,
        sendMessage,
        unreadCounts,
        setUnreadCounts,
      }}
    >
      {children}
      {notify.visible && (
        <AlertResponses
          title={notify.title}
          message={notify.message}
          type={notify.type}
          visible={notify.visible}
          setNotify={setNotify}
        />
      )}
    </NotificationsContext.Provider>
  );
}

export default NotificationsContext;
